import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  BadgeCheck,
  Brain,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  CreditCard,
  FileText,
  FilePenLine,
  Gift,
  Loader2,
  MapPin,
  MessageSquareText,
  Mic,
  ReceiptText,
  Save,
  Search,
  ShieldCheck,
  Stethoscope,
  Plus,
  Trash2,
  UserRound,
  Video,
  XCircle,
} from 'lucide-react';
import InitialAvatar from '../components/InitialAvatar';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiClient } from '../lib/apiClient';
import { SelectMenu } from '../ui/SelectMenu';
import { cn } from '../utils/cn';

type PaymentStatus = 'UNPAID' | 'DUE_AT_PARENT' | 'PAID_AT_PARENT' | 'PACKAGE_REDEEMED' | 'COMPLIMENTARY' | 'WAIVED' | 'NOT_REQUIRED';
type PaymentStatusLike = PaymentStatus | { status?: string | null } | null | undefined;
type StockDeductionMode = 'AUTO_LOAD' | 'MANUAL_LOAD';
type VisitActionItem = {
  key: string;
  label: string;
  description?: string;
  tone?: 'primary' | 'muted' | 'danger';
  busy?: boolean;
  icon: any;
  onClick: () => void;
};

type TelemedNoteDraft = {
  clinicianNotes?: string | null;
  transcriptExcerpt?: string | null;
  diagnosticsSummary?: string | null;
  suggestedNextSteps: string[];
  updatedAt?: string | null;
};

type TelemedSessionSnapshot = {
  enabled: boolean;
  sessionId?: string | null;
  status?: string | null;
  joinUrl?: string | null;
  roomCode?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  summaryNoteId?: string | null;
  updatedAt?: string | null;
  noteDraft?: TelemedNoteDraft | null;
};

type VisitWorkspace = {
  encounter: {
    id: string;
    status: string;
    patientId: string;
    appointmentId?: string | null;
    startedAt?: string | null;
    completedAt?: string | null;
    signedAt?: string | null;
    visitReason?: string;
    visitSummary?: any;
    followUpPlan?: any;
    soapNote?: any;
    vitals?: any;
    diagnoses?: any;
    workspaceVersion: number;
    updatedAt?: string | null;
  };
  patient: {
    id: string;
    name: string;
    avatarUrl?: string | null;
    mrn?: string | null;
    dob?: string | null;
    sex?: string | null;
    phone?: string | null;
    email?: string | null;
    flags: string[];
    allergies: string[];
    medications: string[];
    problems: string[];
  };
  provider: { id: string; name: string };
  appointment?: {
    id: string;
    status: string;
    startTime: string;
    endTime: string;
    notes?: string | null;
    checkedInAt?: string | null;
    appointmentType: { id: string; name: string; isTelemed?: boolean; price: number; currency: string };
    billingService?: { id: string; name: string; category?: string | null; price: number } | null;
    location?: { id: string; name: string } | null;
    telemed?: TelemedSessionSnapshot | null;
    paymentStatus: { status: string; total: number | null; balance: number | null; invoiceNumber?: string | null; invoiceId?: string | null };
  } | null;
  treatmentCompletion?: {
    id: string;
    performedServiceName: string;
    notes?: string | null;
    performedServices?: ServiceLine[];
    paymentStatus: PaymentStatus;
    expectedAmount: number;
    finalAmount: number;
    completedAt?: string | null;
  } | null;
  recentVisits: Array<{
    id: string;
    status: string;
    updatedAt?: string | null;
    completedAt?: string | null;
    provider: { id: string; name: string };
    appointmentType?: string | null;
    visitReason?: string | null;
    visitSummary?: any;
  }>;
  allowedActions: {
    canEdit: boolean;
    canOperate: boolean;
    canComplete: boolean;
    canSign: boolean;
    readOnly: boolean;
  };
  canViewFinancial?: boolean;
  nextActions: Array<{ key: string; label: string; tone: string }>;
};

type TreatmentPayload = {
  appointment: {
    id: string;
    status: string;
    paymentStatus?: PaymentStatusLike;
    patient: { name: string };
    provider: { name: string };
    location?: { id: string; name: string } | null;
    appointmentType: { id: string; name: string; price: number; currency: string; clinicalSafetyRules?: Record<string, any> };
    billingService?: { id: string; name: string; category?: string | null; price: number; stockRecipe?: StockUsage[]; stockDeductionMode?: StockDeductionMode } | null;
  };
  availableServices?: Array<{ id: string; code: string; name: string; category?: string | null; price: number; active: boolean; compatibleWithAppointmentType?: boolean; stockRecipe?: StockUsage[]; stockDeductionMode?: StockDeductionMode }>;
  defaultStockRecipe: StockUsage[];
  completion?: {
    billingServiceId?: string | null;
    performedServiceName: string;
    performedServices?: ServiceLine[];
    notes?: string | null;
    checklist?: Array<{ label: string; checked: boolean }>;
    stockUsage?: StockUsage[];
    paymentStatus: PaymentStatus;
    expectedAmount: number;
    finalAmount: number;
  } | null;
};

type StockUsage = {
  itemId?: string;
  billingProductId?: string;
  lotId?: string;
  itemName: string;
  plannedQuantity?: number;
  actualQuantity: number;
  unit?: string;
  unitId?: string;
  brand?: string;
  lotNumber?: string;
  deviationReason?: string;
  sourceServiceId?: string;
  sourceServiceLabel?: string;
  sourceRecipeIndex?: number;
  autoLoaded?: boolean;
};

type InventorySummaryRow = {
  id: string;
  name: string;
  baseUnit: string;
  baseUnitId?: string | null;
  defaultConsumptionUnitId?: string | null;
  consumableInVisit?: boolean;
  strength?: string | null;
  packageSize?: string | null;
  trackLots?: boolean;
  lotId?: string | null;
  lotNumber?: string | null;
  brand?: string | null;
  available: number;
};

type InventoryItemUnitOption = {
  unitId: string;
  name: string;
  abbreviation?: string | null;
  factorToBaseUnit: number;
  allowedForConsumption: boolean;
  active: boolean;
};

type ServiceLine = {
  serviceId?: string;
  code: string;
  name: string;
  quantity: number;
  unitPrice: number;
};

type ServiceOption = ServiceLine & {
  key: string;
  source: string;
  stockRecipe: StockUsage[];
  stockDeductionMode: StockDeductionMode;
};

type ServicePackage = {
  id: string;
  serviceName: string;
  remainingQuantity: number;
  expiresAt?: string | null;
  status: string;
};

const paymentStatuses: PaymentStatus[] = ['UNPAID', 'DUE_AT_PARENT', 'PAID_AT_PARENT', 'PACKAGE_REDEEMED', 'COMPLIMENTARY', 'WAIVED', 'NOT_REQUIRED'];
const serviceSearchPageSize = 5;
const paymentStatusLabels: Record<PaymentStatus, string> = {
  UNPAID: 'Payment due',
  DUE_AT_PARENT: 'Pay on invoice',
  PAID_AT_PARENT: 'Already paid on invoice',
  PACKAGE_REDEEMED: 'Covered by package',
  COMPLIMENTARY: 'Complimentary',
  WAIVED: 'Waived',
  NOT_REQUIRED: 'No payment required',
};

function normalizeTreatmentPaymentStatus(value: PaymentStatusLike): PaymentStatus {
  if (typeof value === 'string' && paymentStatuses.includes(value as PaymentStatus)) {
    return value as PaymentStatus;
  }

  const summaryStatus = typeof value === 'object' && value && typeof value.status === 'string' ? value.status : null;
  switch (summaryStatus) {
    case 'PAID':
      return 'PAID_AT_PARENT';
    case 'DUE':
    case 'PENDING':
      return 'DUE_AT_PARENT';
    case 'UNREGISTERED':
    default:
      return 'UNPAID';
  }
}

function extractApiErrorMessage(raw: string, fallback: string) {
  let current: unknown = raw;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (typeof current !== 'string') break;
    const trimmed = current.trim();
    if (!trimmed) break;
    if (!(trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"'))) break;
    try {
      current = JSON.parse(trimmed);
    } catch {
      break;
    }
  }

  const message = typeof current === 'string'
    ? current
    : current && typeof current === 'object' && 'message' in current && typeof (current as { message?: unknown }).message === 'string'
      ? (current as { message: string }).message
      : fallback;

  if (message.includes('paymentStatus') && message.includes('Expected')) {
    return 'Treatment could not be saved because the payment status was invalid. Refresh the page and try again.';
  }

  return message || fallback;
}

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function formatMoney(value: number, currency = 'USD') {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(value || 0));
  } catch {
    return `${currency} ${Number(value || 0).toFixed(2)}`;
  }
}

function inventoryItemLabel(item: InventorySummaryRow) {
  return item.name;
}

function inventoryLotLabel(item: InventorySummaryRow) {
  const parts = [item.lotNumber, item.brand].filter(Boolean);
  const quantity = Number(item.available || 0);
  parts.push(`Available ${quantity}${item.baseUnit ? ` ${item.baseUnit}` : ''}`);
  return parts.join(' - ');
}

function normalizeStockUsageRecipe(recipe: StockUsage[], options?: { sourceServiceId?: string; sourceServiceLabel?: string; autoLoaded?: boolean }) {
  return recipe.map((item, index) => ({
    itemId: item.itemId || undefined,
    billingProductId: item.billingProductId || undefined,
    lotId: item.lotId || undefined,
    itemName: item.itemName || 'Stock item',
    plannedQuantity: Number(item.plannedQuantity ?? item.actualQuantity ?? 1),
    actualQuantity: Number(item.actualQuantity ?? item.plannedQuantity ?? 1),
    unit: item.unit || '',
    unitId: item.unitId || undefined,
    brand: item.brand || '',
    lotNumber: item.lotNumber || '',
    deviationReason: item.deviationReason || '',
    sourceServiceId: options?.sourceServiceId,
    sourceServiceLabel: options?.sourceServiceLabel,
    sourceRecipeIndex: index,
    autoLoaded: options?.autoLoaded ?? false,
  }));
}

function stockUsageIdentity(item: Pick<StockUsage, 'sourceServiceId' | 'sourceRecipeIndex' | 'itemId' | 'billingProductId' | 'itemName'>) {
  return [
    item.sourceServiceId || '',
    String(item.sourceRecipeIndex ?? ''),
    item.itemId || '',
    item.billingProductId || '',
    String(item.itemName || '').trim().toLowerCase(),
  ].join('::');
}

function stockUsageRecipeKey(item: Pick<StockUsage, 'itemId' | 'billingProductId' | 'itemName' | 'plannedQuantity' | 'unitId' | 'unit'>) {
  return [
    item.itemId || '',
    item.billingProductId || '',
    String(item.itemName || '').trim().toLowerCase(),
    String(Number(item.plannedQuantity ?? 0)),
    item.unitId || '',
    String(item.unit || '').trim().toLowerCase(),
  ].join('::');
}

function titleCase(value: string) {
  return value.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
}

function getInitials(value: string) {
  return value
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function serviceLineKey(line: Pick<ServiceLine, 'serviceId' | 'code' | 'name'>) {
  return line.serviceId ? `service:${line.serviceId}` : `code:${line.code || line.name}`;
}

function buildServiceOptions(payload: TreatmentPayload | null): ServiceOption[] {
  if (!payload) return [];
  const options = new Map<string, ServiceOption>();
  const add = (line: Omit<ServiceOption, 'key'>) => {
    const key = serviceLineKey(line);
    if (!options.has(key)) options.set(key, { ...line, key });
  };

  add({
    code: payload.appointment.appointmentType.id,
    name: payload.appointment.appointmentType.name,
    quantity: 1,
    unitPrice: Number(payload.appointment.appointmentType.price ?? 0),
    source: 'Scheduled appointment',
    stockRecipe: [],
    stockDeductionMode: 'AUTO_LOAD',
  });

  if (payload.appointment.billingService) {
    add({
      serviceId: payload.appointment.billingService.id,
      code: payload.appointment.billingService.id,
      name: payload.appointment.billingService.name,
      quantity: 1,
      unitPrice: Number(payload.appointment.billingService.price ?? 0),
      source: payload.appointment.billingService.category || 'Selected service',
      stockRecipe: normalizeStockUsageRecipe(payload.appointment.billingService.stockRecipe || []),
      stockDeductionMode: payload.appointment.billingService.stockDeductionMode || 'AUTO_LOAD',
    });
  }

  (payload.availableServices || []).forEach((service) => {
    if (!service.active) return;
    add({
      serviceId: service.id,
      code: service.code || service.id,
      name: service.name,
      quantity: 1,
      unitPrice: Number(service.price ?? 0),
      source: service.compatibleWithAppointmentType === false
        ? service.category ? `Service group: ${service.category}` : 'Service list'
        : service.category || 'Service list',
      stockRecipe: normalizeStockUsageRecipe(service.stockRecipe || []),
      stockDeductionMode: service.stockDeductionMode || 'AUTO_LOAD',
    });
  });

  (payload.completion?.performedServices || []).forEach((line) => {
    add({
      serviceId: line.serviceId,
      code: line.code,
      name: line.name,
      quantity: line.quantity,
      unitPrice: Number(line.unitPrice ?? 0),
      source: 'Saved service',
      stockRecipe: [],
      stockDeductionMode: 'AUTO_LOAD',
    });
  });

  return Array.from(options.values());
}

function workspaceTreatmentPayload(payload: VisitWorkspace): TreatmentPayload | null {
  if (!payload.appointment) return null;
  const price = Number(payload.appointment.billingService?.price ?? payload.appointment.appointmentType.price ?? 0);
  const defaultLine: ServiceLine = {
    serviceId: payload.appointment.billingService?.id,
    code: payload.appointment.billingService?.id || payload.appointment.appointmentType.id,
    name: payload.appointment.billingService?.name || payload.appointment.appointmentType.name,
    quantity: 1,
    unitPrice: price,
  };
  const completionLines = Array.isArray(payload.treatmentCompletion?.performedServices) && payload.treatmentCompletion.performedServices.length
    ? payload.treatmentCompletion.performedServices
    : [defaultLine];

  return {
    appointment: {
      id: payload.appointment.id,
      status: payload.appointment.status,
      paymentStatus: payload.appointment.paymentStatus,
      patient: { name: payload.patient.name },
      provider: { name: payload.provider.name },
      location: payload.appointment.location,
      appointmentType: {
        ...payload.appointment.appointmentType,
        clinicalSafetyRules: {},
      },
      billingService: payload.appointment.billingService,
    },
    availableServices: [],
    defaultStockRecipe: [],
    completion: payload.treatmentCompletion
      ? {
          billingServiceId: payload.appointment.billingService?.id ?? null,
          performedServiceName: payload.treatmentCompletion.performedServiceName || defaultLine.name,
          performedServices: completionLines,
          notes: payload.treatmentCompletion.notes,
          checklist: [],
          stockUsage: [],
          paymentStatus: payload.treatmentCompletion.paymentStatus,
          expectedAmount: Number(payload.treatmentCompletion.expectedAmount ?? price),
          finalAmount: Number(payload.treatmentCompletion.finalAmount ?? price),
        }
      : {
          billingServiceId: payload.appointment.billingService?.id ?? null,
          performedServiceName: defaultLine.name,
          performedServices: [defaultLine],
          notes: '',
          checklist: [],
          stockUsage: [],
          paymentStatus: normalizeTreatmentPaymentStatus(payload.appointment.paymentStatus),
          expectedAmount: price,
          finalAmount: price,
        },
  };
}

function textFromJson(value: any, fallback = '') {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value?.text === 'string') return value.text;
  if (typeof value?.summary === 'string') return value.summary;
  return fallback;
}

function mergeImportedSection(current: string, heading: string, value: string) {
  const incoming = value.trim();
  if (!incoming) return current;
  const normalizedCurrent = current.trim();
  if (!normalizedCurrent) return incoming;
  const section = `${heading}\n${incoming}`;
  if (normalizedCurrent.includes(section) || normalizedCurrent.includes(incoming)) return current;
  return `${normalizedCurrent}\n\n${section}`;
}

function buildTelemedImportDraft(noteDraft: TelemedNoteDraft | null | undefined) {
  if (!noteDraft) {
    return {
      subjective: '',
      assessment: '',
      plan: '',
      followUp: '',
    };
  }
  const subjective = [noteDraft.clinicianNotes, noteDraft.transcriptExcerpt]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join('\n\n');
  const assessment = String(noteDraft.diagnosticsSummary ?? '').trim();
  const nextSteps = Array.isArray(noteDraft.suggestedNextSteps)
    ? noteDraft.suggestedNextSteps.map((step) => String(step || '').trim()).filter(Boolean)
    : [];
  return {
    subjective,
    assessment,
    plan: nextSteps.length ? nextSteps.map((step) => `- ${step}`).join('\n') : '',
    followUp: '',
  };
}

function listPreview(items: string[], empty: string) {
  if (!items.length) return <p className="text-sm text-slate-500">{empty}</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {items.slice(0, 6).map((item) => (
        <span key={item} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700">
          {item}
        </span>
      ))}
    </div>
  );
}

export default function VisitWorkspacePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { fetchJson, fetchApi } = useApiClient();
  const [workspace, setWorkspace] = useState<VisitWorkspace | null>(null);
  const [treatment, setTreatment] = useState<TreatmentPayload | null>(null);
  const [visitReason, setVisitReason] = useState('');
  const [subjective, setSubjective] = useState('');
  const [objective, setObjective] = useState('');
  const [assessment, setAssessment] = useState('');
  const [plan, setPlan] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [selectedServiceKeys, setSelectedServiceKeys] = useState<string[]>([]);
  const [serviceQuantities, setServiceQuantities] = useState<Record<string, number>>({});
  const [servicePrices, setServicePrices] = useState<Record<string, number>>({});
  const [customServices, setCustomServices] = useState<ServiceOption[]>([]);
  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceSearchOpen, setServiceSearchOpen] = useState(false);
  const [serviceSearchVisibleCount, setServiceSearchVisibleCount] = useState(serviceSearchPageSize);
  const [inventoryRows, setInventoryRows] = useState<InventorySummaryRow[]>([]);
  const [inventoryCatalogRows, setInventoryCatalogRows] = useState<InventorySummaryRow[]>([]);
  const [inventoryItemUnits, setInventoryItemUnits] = useState<Record<string, InventoryItemUnitOption[]>>({});
  const [stockUsage, setStockUsage] = useState<StockUsage[]>([]);
  const [didUserChangeServices, setDidUserChangeServices] = useState(false);
  const [treatmentNotes, setTreatmentNotes] = useState('');
  const [echoTranscript, setEchoTranscript] = useState('');
  const [echoNotice, setEchoNotice] = useState<string | null>(null);
  const [echoTranscribing, setEchoTranscribing] = useState(false);
  const [safetyChecks, setSafetyChecks] = useState<Record<string, any>>({});
  const [eligiblePackages, setEligiblePackages] = useState<ServicePackage[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [packageWillBeUsed, setPackageWillBeUsed] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('UNPAID');
  const [finalAmount, setFinalAmount] = useState(0);
  const [finalAmountManuallyAdjusted, setFinalAmountManuallyAdjusted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [treatmentSectionHint, setTreatmentSectionHint] = useState('');
  const [hasOpenedTreatmentShortcut, setHasOpenedTreatmentShortcut] = useState(false);
  const [lastTreatmentFocusKey, setLastTreatmentFocusKey] = useState('');
  const treatmentSectionRef = useRef<HTMLElement | null>(null);
  const visitReasonInputRef = useRef<HTMLInputElement | null>(null);
  const treatmentShortcutOpened = hasOpenedTreatmentShortcut;

  const applyTreatmentPayload = useCallback((treatmentPayload: TreatmentPayload) => {
    setTreatment(treatmentPayload);
    const serviceOptions = buildServiceOptions(treatmentPayload);
    const savedLines = Array.isArray(treatmentPayload.completion?.performedServices) ? treatmentPayload.completion?.performedServices || [] : [];
    const savedKeys = savedLines.map((line) => serviceLineKey(line)).filter((key) => serviceOptions.some((option) => option.key === key));
    const defaultKey = treatmentPayload.appointment.billingService
      ? serviceLineKey({ serviceId: treatmentPayload.appointment.billingService.id, code: treatmentPayload.appointment.billingService.id, name: treatmentPayload.appointment.billingService.name })
      : serviceLineKey({ code: treatmentPayload.appointment.appointmentType.id, name: treatmentPayload.appointment.appointmentType.name });
    setSelectedServiceKeys(savedKeys.length ? savedKeys : [defaultKey]);
    setServiceQuantities(
      Object.fromEntries(
        (savedLines.length ? savedLines : serviceOptions)
          .map((line) => [serviceLineKey(line), Math.max(1, Number(line.quantity || 1))]),
      ),
    );
    setServicePrices(
      Object.fromEntries(
        (savedLines.length ? savedLines : serviceOptions)
          .map((line) => [serviceLineKey(line), Math.max(0, Number(line.unitPrice ?? 0))]),
      ),
    );
    setCustomServices(
      savedLines
        .filter((line) => !line.serviceId && String(line.code || '').startsWith('custom-'))
        .map((line) => ({
          ...line,
          key: serviceLineKey(line),
          source: 'Added during visit',
          stockRecipe: [],
          stockDeductionMode: 'AUTO_LOAD' as const,
        })),
    );
    setTreatmentNotes(treatmentPayload.completion?.notes || '');
    setStockUsage(normalizeStockUsageRecipe(treatmentPayload.completion?.stockUsage?.length ? treatmentPayload.completion.stockUsage : treatmentPayload.defaultStockRecipe));
    setDidUserChangeServices(false);
    const savedSafetyChecks = (treatmentPayload.completion?.checklist?.find((item) => (item as any)?.safetyChecks) as any)?.safetyChecks;
    setSafetyChecks(savedSafetyChecks && typeof savedSafetyChecks === 'object' ? savedSafetyChecks : {});
    setPaymentStatus(normalizeTreatmentPaymentStatus(treatmentPayload.completion?.paymentStatus || treatmentPayload.appointment.paymentStatus));
    setPackageWillBeUsed(normalizeTreatmentPaymentStatus(treatmentPayload.completion?.paymentStatus || treatmentPayload.appointment.paymentStatus) === 'PACKAGE_REDEEMED');
    setFinalAmount(Number(treatmentPayload.completion?.finalAmount ?? treatmentPayload.appointment.billingService?.price ?? treatmentPayload.appointment.appointmentType.price ?? 0));
    setFinalAmountManuallyAdjusted(false);
  }, []);

  const loadWorkspace = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const payload = await fetchJson<VisitWorkspace>(`/encounters/${id}/workspace`);
      setWorkspace(payload);
      setVisitReason(payload.encounter.visitReason || '');
      setSubjective(textFromJson(payload.encounter.soapNote?.subjective));
      setObjective(textFromJson(payload.encounter.soapNote?.objective));
      setAssessment(textFromJson(payload.encounter.soapNote?.assessment));
      setPlan(textFromJson(payload.encounter.soapNote?.plan));
      setFollowUp(textFromJson(payload.encounter.followUpPlan));
      if (payload.appointment?.id) {
        const fallbackTreatment = workspaceTreatmentPayload(payload);
        if (payload.canViewFinancial) {
          try {
            const packagePayload = await fetchJson<{ data: ServicePackage[] }>(
              `/packages?patientId=${encodeURIComponent(payload.patient.id)}&appointmentTypeId=${encodeURIComponent(payload.appointment.appointmentType.id)}`,
            );
            const usablePackages = (packagePayload.data || []).filter((pkg) => pkg.status === 'ACTIVE' && Number(pkg.remainingQuantity || 0) > 0);
            setEligiblePackages(usablePackages);
            setSelectedPackageId((current) => current && usablePackages.some((pkg) => pkg.id === current) ? current : usablePackages[0]?.id || '');
          } catch {
            setEligiblePackages([]);
            setSelectedPackageId('');
          }
        } else {
          setEligiblePackages([]);
          setSelectedPackageId('');
        }
        try {
          const treatmentPayload = await fetchJson<TreatmentPayload>(`/appointments/${payload.appointment.id}/treatment-completion`);
          applyTreatmentPayload(treatmentPayload);
        } catch {
          if (fallbackTreatment) {
            applyTreatmentPayload(fallbackTreatment);
          } else {
            setTreatment(null);
            setSelectedServiceKeys([]);
            setServiceQuantities({});
            setServicePrices({});
            setCustomServices([]);
            setServiceSearch('');
            setStockUsage([]);
            setDidUserChangeServices(false);
            setSafetyChecks({});
          }
        }
      } else {
        setTreatment(null);
        setSelectedServiceKeys([]);
        setServiceQuantities({});
        setServicePrices({});
        setCustomServices([]);
        setServiceSearch('');
        setStockUsage([]);
        setDidUserChangeServices(false);
        setSafetyChecks({});
        setEligiblePackages([]);
        setSelectedPackageId('');
        setPackageWillBeUsed(false);
        setFinalAmountManuallyAdjusted(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load visit workspace.');
    } finally {
      setLoading(false);
    }
  }, [applyTreatmentPayload, fetchJson, id]);

  useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    setTreatmentSectionHint('');
    setHasOpenedTreatmentShortcut(false);
    setLastTreatmentFocusKey('');
  }, [id]);

  useEffect(() => {
    let active = true;
    const locationId = workspace?.appointment?.location?.id || treatment?.appointment.location?.id || '';
    void (async () => {
      try {
        const [catalogPayload, inventoryPayload] = await Promise.all([
          fetchJson<{ data: InventorySummaryRow[] }>('/inventory/summary?includeUnstocked=true'),
          locationId
            ? fetchJson<{ data: InventorySummaryRow[] }>(`/inventory/summary?locationId=${encodeURIComponent(locationId)}`)
            : Promise.resolve({ data: [] as InventorySummaryRow[] }),
        ]);
        if (active) {
          setInventoryCatalogRows(Array.isArray(catalogPayload.data) ? catalogPayload.data : []);
          setInventoryRows(Array.isArray(inventoryPayload.data) ? inventoryPayload.data : []);
        }
      } catch {
        if (active) {
          setInventoryCatalogRows([]);
          setInventoryRows([]);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [fetchJson, treatment?.appointment.location?.id, workspace?.appointment?.location?.id]);

  const loadInventoryItemUnits = useCallback(async (itemId: string) => {
    if (!itemId || inventoryItemUnits[itemId]) return;
    try {
      const payload = await fetchJson<{ data: InventoryItemUnitOption[] }>(`/inventory/items/${itemId}/units`);
      setInventoryItemUnits((current) => ({
        ...current,
        [itemId]: Array.isArray(payload.data) ? payload.data.filter((unit) => unit.active && unit.allowedForConsumption) : [],
      }));
    } catch {
      setInventoryItemUnits((current) => ({ ...current, [itemId]: [] }));
    }
  }, [fetchJson, inventoryItemUnits]);

  useEffect(() => {
    stockUsage.forEach((item) => {
      if (item.itemId && !inventoryItemUnits[item.itemId]) {
        void loadInventoryItemUnits(item.itemId);
      }
    });
  }, [inventoryItemUnits, loadInventoryItemUnits, stockUsage]);

  useEffect(() => {
    setStockUsage((current) => current.map((item) => {
      if (!item.itemId || item.unitId || item.unit) return item;
      const product = inventoryRows.find((row) => row.id === item.itemId)
        || inventoryCatalogRows.find((row) => row.id === item.itemId);
      const units = inventoryItemUnits[item.itemId] || [];
      if (!product || !units.length) return item;
      const defaultUnit = getDefaultStockUsageUnit(product, units);
      return {
        ...item,
        unit: defaultUnit.unit,
        unitId: defaultUnit.unitId,
      };
    }));
  }, [inventoryCatalogRows, inventoryItemUnits, inventoryRows]);

  const readOnly = Boolean(workspace?.allowedActions.readOnly || !workspace?.allowedActions.canEdit);
  const canViewFinancial = workspace?.canViewFinancial !== false;
  const currency = workspace?.appointment?.appointmentType.currency || 'USD';
  const completionState = workspace?.treatmentCompletion ? 'Completed' : 'Pending';
  const paymentBalance = typeof workspace?.appointment?.paymentStatus.balance === 'number' ? workspace.appointment.paymentStatus.balance : null;
  const invoiceNumber = workspace?.appointment?.paymentStatus.invoiceNumber || null;
  const invoiceId = workspace?.appointment?.paymentStatus.invoiceId || null;
  const selectedPackage = eligiblePackages.find((pkg) => pkg.id === selectedPackageId) || null;
  const telemed = workspace?.appointment?.telemed ?? null;
  const telemedNoteDraft = telemed?.noteDraft ?? null;
  const telemedEnabled = Boolean(telemed?.enabled);
  const telemedStatus = telemed?.status || null;
  const telemedLaunchable = telemedEnabled && !['FINISHED', 'ENDED'].includes(String(telemedStatus || '').toUpperCase());
  const telemedActionLabel = ['IN_SESSION', 'IN_VISIT'].includes(String(telemedStatus || '').toUpperCase()) ? 'Rejoin telemed' : 'Start telemed';
  const telemedImportPreview = telemedNoteDraft?.diagnosticsSummary || telemedNoteDraft?.clinicianNotes || telemedNoteDraft?.transcriptExcerpt || '';
  const patientInitials = useMemo(() => getInitials(workspace?.patient.name || 'Patient'), [workspace?.patient.name]);
  const encounterStatus = workspace?.encounter.status || 'UNKNOWN';
  const visitIsFinalized = ['SIGNED', 'LOCKED'].includes(encounterStatus);
  const visitIsCompletable = !['COMPLETED', 'SIGNED', 'LOCKED'].includes(encounterStatus);
  const treatmentRecorded = Boolean(workspace?.treatmentCompletion);
  const visitNoteCompleted = encounterStatus === 'COMPLETED' || visitIsFinalized;
  const visitSigned = encounterStatus === 'SIGNED' || encounterStatus === 'LOCKED';
  const visitActionItems: VisitActionItem[] = workspace
    ? [
        workspace.allowedActions.canOperate && workspace.appointment && ['SCHEDULED', 'PORTAL_BOOKED'].includes(workspace.appointment.status)
          ? {
              key: 'check-in',
              label: 'Check in patient',
              description: 'Mark the appointment as arrived and ready for treatment.',
              icon: CheckCircle2,
              busy: busyAction === 'check-in',
              onClick: () => runVisitAction('check-in'),
            }
          : null,
        workspace.allowedActions.canOperate && visitIsCompletable
          ? {
              key: 'treatment',
              label: workspace.treatmentCompletion ? 'Review treatment and billing' : treatmentShortcutOpened ? 'Continue treatment form' : 'Open treatment form',
              description: 'Record services, stock usage, and the invoice draft for this visit.',
              icon: ShieldCheck,
              busy: busyAction === 'treatment',
              onClick: focusTreatmentSection,
            }
          : null,
        workspace.allowedActions.canOperate && workspace.appointment && telemedLaunchable
          ? {
              key: 'telemed',
              label: telemedActionLabel,
              description: 'Open the telemedicine room for this appointment.',
              icon: Video,
              onClick: () => navigate(`/telemedicine/${workspace.appointment!.id}?role=host`),
            }
          : null,
        workspace.allowedActions.canComplete && visitIsCompletable
          ? {
              key: 'complete',
              label: 'Complete visit note',
              description: 'Mark the encounter documentation as finished.',
              icon: BadgeCheck,
              busy: busyAction === 'complete',
              onClick: completeVisit,
            }
          : null,
        workspace.allowedActions.canSign && encounterStatus === 'COMPLETED'
          ? {
              key: 'sign',
              label: 'Sign visit',
              description: 'Sign and lock visit after final clinician review. Signed visits become read-only.',
              icon: FilePenLine,
              busy: busyAction === 'sign',
              onClick: signVisit,
            }
          : null,
        workspace.allowedActions.canOperate && workspace.appointment && !['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(workspace.appointment.status)
          ? {
              key: 'no-show',
              label: 'Mark no-show',
              description: 'Close the appointment without treatment or billing.',
              icon: AlertCircle,
              tone: 'muted',
              busy: busyAction === 'no-show',
              onClick: () => runVisitAction('no-show'),
            }
          : null,
        workspace.allowedActions.canOperate && workspace.appointment && !['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(workspace.appointment.status)
          ? {
              key: 'cancel',
              label: 'Cancel visit',
              description: 'Cancel this appointment without completing treatment.',
              icon: XCircle,
              tone: 'danger',
              busy: busyAction === 'cancel',
              onClick: () => runVisitAction('cancel'),
            }
          : null,
      ].filter(Boolean) as VisitActionItem[]
    : [];
  const actionStateMessage = readOnly
    ? 'Read-only. No visit actions are available for your role.'
    : visitIsFinalized
      ? 'Visit finalized. No further actions are needed here.'
      : 'No visit actions are available right now.';
  const requiredSafetyChecks = useMemo(() => {
    const rules = treatment?.appointment.appointmentType.clinicalSafetyRules;
    if (!rules || typeof rules !== 'object' || !rules.enabled) return [] as Array<{ key: string; label: string }>;
    const checks: Array<{ rule: string; key: string; label: string }> = [
      { rule: 'requireAllergyReview', key: 'allergiesReviewed', label: 'Allergies reviewed' },
      { rule: 'requireMedicationReview', key: 'medicationsReviewed', label: 'Medications reviewed' },
      { rule: 'requirePregnancyReview', key: 'pregnancyReviewed', label: 'Pregnancy status reviewed' },
      { rule: 'requireContraindicationReview', key: 'contraindicationsReviewed', label: 'Contraindications reviewed' },
      { rule: 'requireSuitabilityConfirmation', key: 'suitabilityConfirmed', label: 'Suitability confirmed' },
      { rule: 'doctorEscalationRequired', key: 'doctorEscalated', label: 'Doctor escalation completed' },
    ];
    return checks.filter((check) => Boolean(rules[check.rule])).map(({ key, label }) => ({ key, label }));
  }, [treatment]);
  const adverseEventEnabled = Boolean(treatment?.appointment.appointmentType.clinicalSafetyRules?.adverseEventRecordingEnabled);
  const serviceOptions = useMemo(() => {
    const combined = new Map<string, ServiceOption>();
    buildServiceOptions(treatment).forEach((option) => combined.set(option.key, option));
    customServices.forEach((option) => combined.set(option.key, option));
    return Array.from(combined.values());
  }, [customServices, treatment]);
  const visibleServiceOptions = useMemo(() => {
    return serviceOptions.filter((option) =>
      selectedServiceKeys.includes(option.key) ||
      option.source === 'Scheduled appointment' ||
      option.source === 'Selected service' ||
      option.source === 'Saved service' ||
      option.source === 'Added during visit',
    );
  }, [selectedServiceKeys, serviceOptions]);
  const filteredServiceSearchResults = useMemo(() => {
    const query = serviceSearch.trim().toLowerCase();
    return serviceOptions
      .filter((option) => !selectedServiceKeys.includes(option.key))
      .filter((option) => {
        if (!query) return option.source !== 'Scheduled appointment';
        return [option.name, option.code, option.source].join(' ').toLowerCase().includes(query);
      });
  }, [selectedServiceKeys, serviceOptions, serviceSearch]);
  const serviceSearchResults = useMemo(
    () => filteredServiceSearchResults.slice(0, serviceSearchVisibleCount),
    [filteredServiceSearchResults, serviceSearchVisibleCount],
  );
  const inventoryItems = useMemo(() => {
    const seen = new Set<string>();
    return inventoryRows.filter((row) => {
      if (!row.id || seen.has(row.id)) return false;
      if (row.consumableInVisit === false || Number(row.available || 0) <= 0) return false;
      seen.add(row.id);
      return true;
    });
  }, [inventoryRows]);
  const inventoryCatalogItems = useMemo(() => {
    const seen = new Set<string>();
    return inventoryCatalogRows.filter((row) => {
      if (!row.id || seen.has(row.id)) return false;
      if (row.consumableInVisit === false) return false;
      seen.add(row.id);
      return true;
    });
  }, [inventoryCatalogRows]);
  const treatmentLocationName = workspace?.appointment?.location?.name || treatment?.appointment.location?.name || 'this visit location';
  const stockUsageAvailability = !treatment?.appointment.location?.id && !workspace?.appointment?.location?.id
    ? 'missing-location'
    : inventoryItems.length > 0
      ? 'ready'
      : inventoryCatalogItems.length > 0
        ? 'no-stock'
        : 'no-products';
  const canAddStockUsage = stockUsageAvailability === 'ready';
  const stockUsageDisabledTitle = stockUsageAvailability === 'missing-location'
    ? 'Assign a visit location before recording product consumption.'
    : stockUsageAvailability === 'no-products'
      ? 'Add products in Inventory Setup before recording visit consumption.'
      : stockUsageAvailability === 'no-stock'
        ? `No stocked products are available at ${treatmentLocationName} yet. Receive stock or complete an opening count first.`
        : 'Add product usage row';
  const selectedServiceOptions = useMemo(
    () => selectedServiceKeys.map((key) => serviceOptions.find((option) => option.key === key)).filter(Boolean) as ServiceOption[],
    [selectedServiceKeys, serviceOptions],
  );
  const autoLoadServices = useMemo(
    () => selectedServiceOptions.filter((service) => service.stockRecipe.length && service.stockDeductionMode === 'AUTO_LOAD' && service.serviceId),
    [selectedServiceOptions],
  );
  const manualLoadServices = useMemo(
    () => selectedServiceOptions.filter((service) => service.stockRecipe.length && service.stockDeductionMode === 'MANUAL_LOAD'),
    [selectedServiceOptions],
  );
  const canManualLoadServiceProducts = manualLoadServices.length > 0 && canAddStockUsage;
  const selectedServiceLines = useMemo<ServiceLine[]>(() => {
    return selectedServiceKeys
      .map((key) => serviceOptions.find((option) => option.key === key))
      .filter(Boolean)
      .map((option) => ({
        serviceId: option!.serviceId,
        code: option!.code,
        name: option!.name,
        quantity: Math.max(1, Number(serviceQuantities[option!.key] || option!.quantity || 1)),
        unitPrice: Math.max(0, Number(servicePrices[option!.key] ?? option!.unitPrice ?? 0)),
      }));
  }, [selectedServiceKeys, serviceOptions, servicePrices, serviceQuantities]);
  const selectedServiceTotal = useMemo(
    () => selectedServiceLines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0),
    [selectedServiceLines],
  );

  const clinicalSummary = useMemo(() => {
    return {
      subjective,
      objective,
      assessment,
      plan,
    };
  }, [assessment, objective, plan, subjective]);

  useEffect(() => {
    if (!selectedServiceLines.length) return;
    if (finalAmountManuallyAdjusted) return;
    setFinalAmount(selectedServiceTotal);
  }, [finalAmountManuallyAdjusted, selectedServiceLines.length, selectedServiceTotal]);

  useEffect(() => {
    if (treatment?.completion?.stockUsage?.length && !didUserChangeServices) return;
    const desiredAutoRows = autoLoadServices.flatMap((service) => normalizeStockUsageRecipe(service.stockRecipe, {
      sourceServiceId: service.serviceId,
      sourceServiceLabel: service.name,
      autoLoaded: true,
    }));
    setStockUsage((current) => {
      const desiredRecipeKeys = new Set(desiredAutoRows.map((row) => stockUsageRecipeKey(row)));
      const currentAutoRows = current.filter((item) => item.autoLoaded);
      const matchedPersistedAutoRows = current
        .filter((item) => !item.autoLoaded && desiredRecipeKeys.has(stockUsageRecipeKey(item)))
        .map((item) => ({ ...item, autoLoaded: true }));
      const autoCandidates = [...currentAutoRows, ...matchedPersistedAutoRows];
      const usedCandidateIndexes = new Set<number>();
      const manualRows = current.filter((item) => !item.autoLoaded && !desiredRecipeKeys.has(stockUsageRecipeKey(item)));
      const nextAutoRows = desiredAutoRows.map((row) => {
        const existingIndex = autoCandidates.findIndex((entry, index) => {
          if (usedCandidateIndexes.has(index)) return false;
          return stockUsageIdentity(entry) === stockUsageIdentity(row) || stockUsageRecipeKey(entry) === stockUsageRecipeKey(row);
        });
        const existing = existingIndex >= 0 ? autoCandidates[existingIndex] : undefined;
        if (existingIndex >= 0) usedCandidateIndexes.add(existingIndex);
        return existing
          ? {
            ...row,
            actualQuantity: existing.actualQuantity,
            unit: existing.unit || row.unit,
            unitId: existing.unitId || row.unitId,
            lotId: existing.lotId,
            lotNumber: existing.lotNumber || row.lotNumber,
            brand: existing.brand || row.brand,
            deviationReason: existing.deviationReason || '',
          }
          : row;
      });
      return [...manualRows, ...nextAutoRows];
    });
  }, [autoLoadServices, didUserChangeServices, treatment?.completion?.stockUsage]);

  function toggleService(key: string) {
    setDidUserChangeServices(true);
    setSelectedServiceKeys((current) => {
      if (current.includes(key)) {
        return current.length > 1 ? current.filter((item) => item !== key) : current;
      }
      return [...current, key];
    });
  }

  function selectServiceOption(service: ServiceOption) {
    setDidUserChangeServices(true);
    setSelectedServiceKeys((current) => current.includes(service.key) ? current : [...current, service.key]);
    setServiceQuantities((current) => ({ ...current, [service.key]: current[service.key] || service.quantity || 1 }));
    setServicePrices((current) => ({ ...current, [service.key]: current[service.key] ?? Number(service.unitPrice || 0) }));
    setServiceSearch('');
    setServiceSearchOpen(false);
    setServiceSearchVisibleCount(serviceSearchPageSize);
  }

  function updateServiceQuantity(key: string, quantity: number) {
    setServiceQuantities((current) => ({ ...current, [key]: Math.max(1, Number(quantity || 1)) }));
  }

  function updateServicePrice(key: string, price: number) {
    setServicePrices((current) => ({ ...current, [key]: Math.max(0, Number(price || 0)) }));
  }

  function updateCustomServiceName(key: string, name: string) {
    setCustomServices((current) => current.map((service) => {
      if (service.key !== key) return service;
      const nextName = name || 'Additional service';
      return { ...service, name: nextName };
    }));
  }

  function addCustomService() {
    const code = `custom-${Date.now()}`;
    const service: ServiceOption = {
      key: `code:${code}`,
      code,
      name: 'Additional service',
      quantity: 1,
      unitPrice: 0,
      source: 'Added during visit',
      stockRecipe: [],
      stockDeductionMode: 'AUTO_LOAD',
    };
    setDidUserChangeServices(true);
    setCustomServices((current) => [...current, service]);
    setSelectedServiceKeys((current) => [...current, service.key]);
    setServiceQuantities((current) => ({ ...current, [service.key]: 1 }));
    setServicePrices((current) => ({ ...current, [service.key]: 0 }));
    setServiceSearch('');
    setServiceSearchOpen(false);
    setServiceSearchVisibleCount(serviceSearchPageSize);
  }

  function handleServiceSearchScroll(event: React.UIEvent<HTMLDivElement>) {
    const target = event.currentTarget;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining > 16) return;
    setServiceSearchVisibleCount((current) =>
      Math.min(current + serviceSearchPageSize, filteredServiceSearchResults.length),
    );
  }

  function removeCustomService(key: string) {
    setDidUserChangeServices(true);
    setCustomServices((current) => current.filter((service) => service.key !== key));
    setSelectedServiceKeys((current) => current.filter((item) => item !== key));
    setServiceQuantities((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setServicePrices((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  function updateStockUsage(index: number, patch: Partial<StockUsage>) {
    setStockUsage((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)));
  }

  function getDefaultStockUsageUnit(item: InventorySummaryRow | undefined, units: InventoryItemUnitOption[]) {
    if (!item) return { unit: '', unitId: undefined as string | undefined };
    const preferred = units.find((unit) => unit.unitId === item.defaultConsumptionUnitId)
      || units.find((unit) => unit.unitId === item.baseUnitId)
      || units[0];
    return {
      unit: preferred?.abbreviation || preferred?.name || item.baseUnit || '',
      unitId: preferred?.unitId || item.defaultConsumptionUnitId || item.baseUnitId || undefined,
    };
  }

  function selectInventoryItem(index: number, itemId: string) {
    const selected = inventoryItems.find((item) => item.id === itemId);
    const units = selected ? (inventoryItemUnits[selected.id] || []) : [];
    const defaultUnit = getDefaultStockUsageUnit(selected, units);
    if (selected && !inventoryItemUnits[selected.id]) {
      void loadInventoryItemUnits(selected.id);
    }
    updateStockUsage(index, {
      itemId: itemId || undefined,
      itemName: selected?.name || '',
      unit: defaultUnit.unit,
      unitId: defaultUnit.unitId,
      lotId: undefined,
      lotNumber: '',
      brand: selected?.brand || '',
    });
  }

  function selectStockLot(index: number, itemId: string | undefined, lotId: string) {
    if (!itemId) {
      updateStockUsage(index, { lotId: undefined, lotNumber: '' });
      return;
    }
    const selectedLot = inventoryRows.find((row) => row.id === itemId && (row.lotId || '') === lotId);
    updateStockUsage(index, {
      lotId: lotId || undefined,
      lotNumber: selectedLot?.lotNumber || '',
      brand: selectedLot?.brand || '',
    });
  }

  function selectStockUsageUnit(index: number, unitId: string) {
    const current = stockUsage[index];
    const units = inventoryItemUnits[current?.itemId || ''] || [];
    const selectedUnit = units.find((unit) => unit.unitId === unitId);
    updateStockUsage(index, {
      unitId: unitId || undefined,
      unit: selectedUnit?.abbreviation || selectedUnit?.name || current?.unit || '',
    });
  }

  function loadSelectedServiceProducts() {
    if (!manualLoadServices.length) return;
    setStockUsage((current) => {
      const existingKeys = new Set(current.map((item) => stockUsageIdentity(item)));
      const additions = manualLoadServices
        .flatMap((service) => normalizeStockUsageRecipe(service.stockRecipe, {
          sourceServiceId: service.serviceId,
          sourceServiceLabel: service.name,
          autoLoaded: false,
        }))
        .filter((item) => !existingKeys.has(stockUsageIdentity(item)));
      return additions.length ? [...current, ...additions] : current;
    });
  }

  function focusTreatmentField(key: string) {
    const target = document.querySelector<HTMLElement>(`[data-treatment-focus="${key}"]`);
    if (!target) return false;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    requestAnimationFrame(() => {
      if ('focus' in target) {
        (target as HTMLElement).focus({ preventScroll: true });
      }
    });
    return true;
  }

  function nextIncompleteTreatmentField() {
    if (!visitReason.trim()) return 'visit-reason';
    if (!subjective.trim()) return 'subjective';
    if (!objective.trim()) return 'objective';
    if (!assessment.trim()) return 'assessment';
    if (!plan.trim()) return 'plan';
    if (!followUp.trim()) return 'follow-up';
    return 'service-search';
  }

  function focusTreatmentSection() {
    const focusKey = lastTreatmentFocusKey || nextIncompleteTreatmentField();
    setHasOpenedTreatmentShortcut(true);
    setTreatmentSectionHint(
      lastTreatmentFocusKey
        ? 'Continue treatment returned you to the last field you were editing.'
        : 'Treatment opened at the next field that still needs input.',
    );
    if (focusTreatmentField(focusKey)) return;
    treatmentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    requestAnimationFrame(() => {
      visitReasonInputRef.current?.focus({ preventScroll: true });
    });
  }

  function openInvoice() {
    const patientId = workspace?.patient.id;
    if (!patientId) {
      navigate('/billing');
      return;
    }
    const params = new URLSearchParams({ patientId });
    if (invoiceId) params.set('invoiceId', invoiceId);
    navigate(`/billing?${params.toString()}`);
  }

  function useSelectedPackageForVisit() {
    if (!selectedPackageId) {
      setError('No eligible package is available for this visit service.');
      return;
    }
    setPackageWillBeUsed(true);
    setPaymentStatus('PACKAGE_REDEEMED');
    setFinalAmount(0);
    setFinalAmountManuallyAdjusted(true);
    setNotice('Package credit will be consumed when you save the treatment.');
  }

  function handleFinalAmountChange(value: number) {
    setFinalAmount(Math.max(0, Number(value || 0)));
    setFinalAmountManuallyAdjusted(true);
  }

  function importTelemedNotes() {
    if (!telemedNoteDraft) {
      setNotice('No telemed notes are available to import.');
      return;
    }

    const imported = buildTelemedImportDraft(telemedNoteDraft);
    if (!imported.subjective && !imported.assessment && !imported.plan && !imported.followUp) {
      setNotice('No telemed notes are available to import.');
      return;
    }

    setSubjective((current) => mergeImportedSection(current, 'Telemed notes', imported.subjective));
    setAssessment((current) => mergeImportedSection(current, 'Telemed assessment', imported.assessment));
    setPlan((current) => mergeImportedSection(current, 'Telemed plan', imported.plan));
    if (imported.followUp) {
      setFollowUp((current) => mergeImportedSection(current, 'Telemed follow-up', imported.followUp));
    }
    setError('');
    setNotice('Telemed notes imported into the visit draft. Save draft to keep them.');
  }

  function runEchoTreatmentNote() {
    const generatedSubjective = subjective || echoTranscript || telemedNoteDraft?.transcriptExcerpt || 'Patient conversation captured for clinical review.';
    const generatedAssessment = assessment || 'AI-ready assessment summary generated from current notes and transcript.';
    const generatedPlan = plan || 'Review medications, confirm safety checks, provide aftercare, and schedule follow-up.';
    setSubjective(generatedSubjective);
    setAssessment(generatedAssessment);
    setPlan(generatedPlan);
    setTreatmentNotes((current) => current || 'Echo AI generated a structured treatment note draft. Review before signing.');
    setEchoNotice('Treatment-note AI draft inserted. Review and save the visit draft.');
  }

  function formatAiReadyTemplate() {
    setSubjective((current) => current || 'Subjective: ');
    setObjective((current) => current || 'Objective: ');
    setAssessment((current) => current || 'Assessment: ');
    setPlan((current) => current || 'Plan: ');
    setFollowUp((current) => current || 'Follow-up: ');
    setEchoNotice('AI-ready SOAP sections prepared.');
  }

  function toggleEchoTranscription() {
    setEchoTranscribing((current) => {
      const next = !current;
      if (next && !echoTranscript) {
        setEchoTranscript('Live voice note: patient reports improvement, mild tenderness, no adverse reaction.');
      }
      setEchoNotice(next ? 'Voice note transcription started.' : 'Voice note transcription stopped.');
      return next;
    });
  }

  function summarizeConversation() {
    const summary = [subjective, objective, assessment, plan, echoTranscript].filter(Boolean).join(' ');
    setAssessment((current) => current || `Consultation summary: ${summary.slice(0, 180) || 'No conversation content captured yet.'}`);
    setEchoNotice('Consultation summary generated from notes and transcript.');
  }

  async function saveDraft(): Promise<number | null> {
    if (!workspace) return null;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await fetchApi(`/encounters/${workspace.encounter.id}/workspace`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expectedWorkspaceVersion: workspace.encounter.workspaceVersion,
          visitReason,
          soapNote: clinicalSummary,
          visitSummary: { summary: assessment || subjective || visitReason },
          followUpPlan: { text: followUp },
        }),
      });
      if (!response.ok) throw new Error(extractApiErrorMessage(await response.text(), 'Unable to save visit.'));
      const result = (await response.json()) as { workspaceVersion?: number };
      setWorkspace((current) => {
        if (!current) return current;
        return {
          ...current,
          encounter: {
            ...current.encounter,
            workspaceVersion: result.workspaceVersion ?? current.encounter.workspaceVersion,
            visitReason,
            soapNote: clinicalSummary,
            visitSummary: { summary: assessment || subjective || visitReason },
            followUpPlan: { text: followUp },
          },
        };
      });
      setNotice('Visit draft saved.');
      return result.workspaceVersion ?? null;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save visit.');
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function runVisitAction(action: 'check-in' | 'no-show' | 'cancel') {
    if (!workspace) return;
    setBusyAction(action);
    setError('');
    setNotice('');
    try {
      const response = await fetchApi(`/encounters/${workspace.encounter.id}/actions/${action}`, { method: 'POST' });
      if (!response.ok) throw new Error(extractApiErrorMessage(await response.text(), 'Unable to update visit.'));
      setNotice(action === 'check-in' ? 'Patient checked in.' : 'Appointment updated.');
      await loadWorkspace();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update visit.');
    } finally {
      setBusyAction(null);
    }
  }

  async function completeTreatment() {
    if (!workspace?.appointment || !treatment) return;
    setBusyAction('treatment');
    setError('');
    setNotice('');
    try {
      const selectedNames = selectedServiceLines.map((line) => line.name);
      const expectedAmount = selectedServiceTotal;
      const preparedStockUsage = stockUsage
        .filter((item) => item.itemId || item.itemName.trim().length > 0)
        .map(({ sourceServiceId, sourceServiceLabel, sourceRecipeIndex, autoLoaded, ...item }) => item);
      const bookedBillingServiceId = treatment.appointment.billingService?.id;
      const completedBookedServiceId = bookedBillingServiceId && selectedServiceLines.some((line) => line.serviceId === bookedBillingServiceId)
        ? bookedBillingServiceId
        : undefined;
      const response = await fetchApi(`/appointments/${workspace.appointment.id}/treatment-completion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          encounterId: workspace.encounter.id,
          billingServiceId: completedBookedServiceId,
          performedServiceName: selectedNames.join(', ') || treatment.completion?.performedServiceName || treatment.appointment.billingService?.name || treatment.appointment.appointmentType.name,
          performedServices: selectedServiceLines,
          notes: treatmentNotes,
          checklist: treatment.completion?.checklist || [],
          safetyChecks,
          stockUsage: preparedStockUsage,
          paymentStatus,
          expectedAmount,
          finalAmount,
          packageId: packageWillBeUsed && selectedPackageId ? selectedPackageId : undefined,
          packageQuantity: packageWillBeUsed && selectedPackageId ? 1 : undefined,
        }),
      });
      if (!response.ok) throw new Error(extractApiErrorMessage(await response.text(), 'Unable to save treatment.'));
      setNotice(canViewFinancial ? 'Treatment saved and invoice lines updated.' : 'Treatment saved.');
      await loadWorkspace();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save treatment from visit.');
    } finally {
      setBusyAction(null);
    }
  }

  async function completeVisit() {
    if (!workspace) return;
    const savedVersion = await saveDraft();
    if (!savedVersion) return;
    setBusyAction('complete');
    setError('');
    setNotice('');
    try {
      const response = await fetchApi(`/encounters/${workspace.encounter.id}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expectedWorkspaceVersion: savedVersion }),
      });
      if (!response.ok) throw new Error(extractApiErrorMessage(await response.text(), 'Unable to complete visit.'));
      setNotice('Visit completed.');
      await loadWorkspace();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to complete visit.');
    } finally {
      setBusyAction(null);
    }
  }

  async function signVisit() {
    if (!workspace) return;
    setBusyAction('sign');
    setError('');
    setNotice('');
    try {
      const response = await fetchApi(`/encounters/${workspace.encounter.id}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expectedWorkspaceVersion: workspace.encounter.workspaceVersion }),
      });
      if (!response.ok) throw new Error(extractApiErrorMessage(await response.text(), 'Unable to sign visit.'));
      setNotice('Visit signed.');
      await loadWorkspace();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sign visit.');
    } finally {
      setBusyAction(null);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6 text-slate-600">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading visit workspace...
      </div>
    );
  }

  if (!workspace) {
    return <div className="p-6 text-sm text-rose-700">{error || 'Visit workspace could not be loaded.'}</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="w-full px-6 py-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4">
          <button
            type="button"
            onClick={() => navigate('/appointments')}
            className="inline-flex w-fit items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900"
          >
            <ArrowLeft size={16} />
            Appointments
          </button>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-4">
                <InitialAvatar name={workspace.patient.name} src={workspace.patient.avatarUrl} alt={workspace.patient.name} className="h-12 w-12 text-sm" />
                <div className="min-w-0">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                      {titleCase(workspace.encounter.status)}
                    </span>
                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                      Visit workspace
                    </span>
                  </div>
                  <h1 className="text-xl font-semibold tracking-tight text-slate-950">{workspace.patient.name}</h1>
                  <p className="mt-1 text-xs text-slate-600">
                    {workspace.appointment?.appointmentType.name || 'Visit'} with {workspace.provider.name} · {formatDateTime(workspace.appointment?.startTime)}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
                      <Stethoscope className="h-3.5 w-3.5 text-emerald-600" />
                      {workspace.provider.name}
                    </span>
                    {workspace.appointment?.location?.name ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
                        <MapPin className="h-3.5 w-3.5 text-cyan-600" />
                        {workspace.appointment.location.name}
                      </span>
                    ) : null}
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
                      <CalendarDays className="h-3.5 w-3.5 text-slate-500" />
                      Updated {formatDateTime(workspace.encounter.updatedAt || workspace.appointment?.startTime)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <StatusTile label="Appointment" value={titleCase(workspace.appointment?.status || 'No appointment')} tone="slate" />
              <StatusTile label="Treatment" value={completionState} tone="emerald" />
              <StatusTile
                label="Payment"
                value={!canViewFinancial ? 'Restricted' : paymentBalance && paymentBalance > 0 ? `${formatMoney(paymentBalance, currency)} due` : titleCase(workspace.appointment?.paymentStatus.status || 'Unregistered')}
                tone="amber"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5">
          {(error || notice) && (
            <div
              className={cn(
                'rounded-2xl border px-4 py-4 text-sm shadow-sm',
                error ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800',
              )}
            >
              <div className="flex items-start gap-3">
                {error ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> : <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0" />}
                <div>
                  <p className="font-semibold">{error ? 'Attention needed' : 'Saved'}</p>
                  <p className="mt-0.5">{error || notice}</p>
                </div>
              </div>
            </div>
          )}

          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4 text-emerald-600" />
                    <h2 className="text-sm font-semibold text-slate-950">Visit actions</h2>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">Treatment records services, stock, and the invoice draft. Complete finishes the note. Sign locks the visit.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                    Encounter {titleCase(encounterStatus)}
                  </span>
                  {workspace.appointment ? (
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                      Appointment {titleCase(workspace.appointment.status)}
                    </span>
                  ) : null}
                  {readOnly ? (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700">
                      Read only
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="p-3">
              <div className="mb-3 grid gap-2 md:grid-cols-3">
                <StatusTile label="Treatment" value={treatmentRecorded ? 'Recorded and billed' : 'Still needed'} tone={treatmentRecorded ? 'emerald' : 'slate'} />
                <StatusTile label="Visit note" value={visitNoteCompleted ? 'Completed' : 'In progress'} tone={visitNoteCompleted ? 'emerald' : 'amber'} />
                <StatusTile label="Signature" value={visitSigned ? 'Signed and locked' : 'Pending clinician sign-off'} tone={visitSigned ? 'emerald' : 'amber'} />
              </div>
              {visitActionItems.length ? (
                <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                  {visitActionItems.map((action) => (
                    <ActionButton
                      key={action.key}
                      icon={action.icon}
                      label={action.label}
                      description={action.description}
                      tone={action.tone}
                      busy={action.busy}
                      onClick={action.onClick}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-3 py-3 text-sm text-slate-600">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm">
                    {visitIsFinalized ? <BadgeCheck className="h-4 w-4" /> : readOnly ? <ShieldCheck className="h-4 w-4" /> : <ClipboardCheck className="h-4 w-4" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-800">{actionStateMessage}</p>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section ref={treatmentSectionRef} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Documentation</p>
                <h2 className="text-sm font-semibold text-slate-950">Clinical note</h2>
                <p className="mt-1 text-xs text-slate-500">Capture the important details for review and follow-up.</p>
                {treatmentSectionHint ? (
                  <p className="mt-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-medium text-sky-800">
                    {treatmentSectionHint}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={saveDraft}
                disabled={saving || readOnly}
                className="inline-flex items-center gap-2 rounded-lg bg-ink px-3 py-2 text-xs font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
                Save draft
              </button>
            </div>

            <div className="space-y-4">
              {telemedNoteDraft ? (
                <div className="rounded-xl border border-sky-200 bg-sky-50/60 px-3 py-3">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-white px-2.5 py-1 text-[11px] font-medium text-sky-700">
                          <Video className="h-3.5 w-3.5" />
                          {telemedStatus ? titleCase(telemedStatus) : 'Telemed note available'}
                        </span>
                        {telemedNoteDraft.updatedAt ? (
                          <span className="text-[11px] text-slate-500">Updated {formatDateTime(telemedNoteDraft.updatedAt)}</span>
                        ) : null}
                      </div>
                      <p className="mt-2 text-sm text-slate-700">{telemedImportPreview || 'Notes from the telemedicine session are available for this visit.'}</p>
                    </div>
                    {!readOnly ? (
                      <button
                        type="button"
                        onClick={importTelemedNotes}
                        className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-white px-3 py-2 text-xs font-medium text-sky-700 transition hover:bg-sky-50"
                      >
                        <ClipboardCheck size={14} />
                        Import telemed notes
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <div className="rounded-xl border border-violet-200 bg-violet-50/60 px-3 py-3">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Brain className="h-4 w-4 text-violet-700" />
                      <h3 className="text-sm font-semibold text-violet-950">Echo AI</h3>
                    </div>
                    <p className="mt-1 text-xs text-violet-800">Generate treatment notes, format SOAP templates, transcribe voice notes, and summarize patient conversations.</p>
                    {echoNotice ? <p className="mt-2 text-xs font-medium text-violet-900">{echoNotice}</p> : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={runEchoTreatmentNote} disabled={readOnly} className="inline-flex items-center gap-1 rounded-lg bg-violet-700 px-3 py-2 text-xs font-medium text-white disabled:opacity-50">
                      <FileText size={14} /> Generate note
                    </button>
                    <button type="button" onClick={formatAiReadyTemplate} disabled={readOnly} className="inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-white px-3 py-2 text-xs font-medium text-violet-800 disabled:opacity-50">
                      <FilePenLine size={14} /> Format template
                    </button>
                    <button type="button" onClick={toggleEchoTranscription} disabled={readOnly} className="inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-white px-3 py-2 text-xs font-medium text-violet-800 disabled:opacity-50">
                      <Mic size={14} /> {echoTranscribing ? 'Stop transcript' : 'Voice transcript'}
                    </button>
                    <button type="button" onClick={summarizeConversation} disabled={readOnly} className="inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-white px-3 py-2 text-xs font-medium text-violet-800 disabled:opacity-50">
                      <MessageSquareText size={14} /> Summarize
                    </button>
                  </div>
                </div>
                <textarea
                  data-treatment-focus="echo-transcript"
                  onFocus={() => setLastTreatmentFocusKey('echo-transcript')}
                  className="mt-3 min-h-20 w-full rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm"
                  value={echoTranscript}
                  onChange={(event) => setEchoTranscript(event.target.value)}
                  disabled={readOnly}
                  placeholder="Dictate or paste patient conversation transcript..."
                />
              </div>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Reason for visit</span>
                <input
                  ref={visitReasonInputRef}
                  data-treatment-focus="visit-reason"
                  value={visitReason}
                  onChange={(event) => setVisitReason(event.target.value)}
                  onFocus={() => setLastTreatmentFocusKey('visit-reason')}
                  disabled={readOnly}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50"
                  placeholder="Why is the patient here today?"
                />
              </label>
              <div className="grid gap-4 lg:grid-cols-2">
                <NoteBox label="Subjective" value={subjective} onChange={setSubjective} disabled={readOnly} placeholder="Patient concerns, symptoms, relevant history" focusKey="subjective" onFocusField={setLastTreatmentFocusKey} />
                <NoteBox label="Objective" value={objective} onChange={setObjective} disabled={readOnly} placeholder="Vitals, observations, relevant findings" focusKey="objective" onFocusField={setLastTreatmentFocusKey} />
                <NoteBox label="Assessment" value={assessment} onChange={setAssessment} disabled={readOnly} placeholder="Clinical impression or working assessment" focusKey="assessment" onFocusField={setLastTreatmentFocusKey} />
                <NoteBox label="Plan" value={plan} onChange={setPlan} disabled={readOnly} placeholder="Treatment plan, next steps, instructions" focusKey="plan" onFocusField={setLastTreatmentFocusKey} />
              </div>
              <NoteBox label="Follow-up" value={followUp} onChange={setFollowUp} disabled={readOnly} placeholder="Follow-up timing, tasks, patient instructions" rows={3} focusKey="follow-up" onFocusField={setLastTreatmentFocusKey} />
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <Stethoscope className="h-4 w-4 text-cyan-600" />
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Service</p>
                <h2 className="text-sm font-semibold text-slate-950">Treatment and payment</h2>
                <p className="mt-1 text-xs text-slate-500">Select what was delivered. Notes are only for exceptions or extra context.</p>
              </div>
            </div>
            {workspace.appointment ? (
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_240px]">
                <div className="space-y-4">
                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className="text-xs font-medium text-slate-700">Services performed</span>
                      <span className="text-xs text-slate-500">{selectedServiceLines.length} selected</span>
                    </div>
                    <div className="relative mb-3">
                      <label className="block">
                        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Add service to bill</span>
                        <div className="relative">
                          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                          <input
                            data-treatment-focus="service-search"
                            value={serviceSearch}
                            onChange={(event) => {
                              setServiceSearch(event.target.value);
                              setServiceSearchOpen(true);
                              setServiceSearchVisibleCount(serviceSearchPageSize);
                            }}
                            onFocus={() => {
                              setLastTreatmentFocusKey('service-search');
                              setServiceSearchOpen(true);
                              setServiceSearchVisibleCount(serviceSearchPageSize);
                            }}
                            onBlur={() => window.setTimeout(() => setServiceSearchOpen(false), 120)}
                            disabled={readOnly}
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 pl-9 text-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50"
                            placeholder="Search services by name, code, or category"
                          />
                        </div>
                      </label>
                      {serviceSearchOpen ? (
                        <div
                          className="absolute left-0 right-0 top-full z-30 mt-2 max-h-72 overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-xl ring-1 ring-slate-100"
                          onScroll={handleServiceSearchScroll}
                        >
                          {serviceSearchResults.length ? serviceSearchResults.map((service) => (
                            <button
                              key={service.key}
                              type="button"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => selectServiceOption(service)}
                              className="flex w-full items-center justify-between gap-3 border-b border-slate-100 px-3 py-2.5 text-left transition hover:bg-slate-50 last:border-b-0"
                            >
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-medium text-slate-900">{service.name}</span>
                                <span className="block truncate text-xs text-slate-500">{service.code} · {service.source}</span>
                              </span>
                              <span className="shrink-0 text-sm font-semibold text-slate-700">{formatMoney(service.unitPrice, currency)}</span>
                            </button>
                          )) : (
                            <div className="px-3 py-2 text-sm text-slate-500">No matching service. Use custom service below.</div>
                          )}
                          {filteredServiceSearchResults.length > serviceSearchResults.length ? (
                            <div className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
                              Scroll to load more services
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      {visibleServiceOptions.map((service) => {
                        const selected = selectedServiceKeys.includes(service.key);
                        const isCustom = service.source === 'Added during visit';
                        return (
                          <div
                            key={service.key}
                            className={cn(
                              'grid gap-3 rounded-xl border p-3 transition md:grid-cols-[minmax(0,1fr)_92px_116px_36px]',
                              selected ? 'border-emerald-300 bg-emerald-50/60' : 'border-slate-200 bg-white',
                            )}
                          >
                            <label className="flex min-w-0 items-start gap-3">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleService(service.key)}
                                disabled={readOnly}
                                className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600"
                              />
                              <span className="min-w-0">
                                {isCustom ? (
                                  <input
                                    value={service.name}
                                    onChange={(event) => updateCustomServiceName(service.key, event.target.value)}
                                    disabled={readOnly}
                                    className="block w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm font-semibold text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                                    placeholder="Service name"
                                  />
                                ) : (
                                  <span className="block truncate text-sm font-semibold text-slate-900">{service.name}</span>
                                )}
                                <span className="block text-xs text-slate-500">{service.source}</span>
                              </span>
                            </label>
                            <label className="block">
                              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Qty</span>
                              <input
                                type="number"
                                min={1}
                                value={serviceQuantities[service.key] || service.quantity || 1}
                                onChange={(event) => updateServiceQuantity(service.key, Number(event.target.value))}
                                disabled={readOnly || !selected}
                                className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm"
                              />
                            </label>
                            <div>
                              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Price</span>
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                value={servicePrices[service.key] ?? service.unitPrice ?? 0}
                                onChange={(event) => updateServicePrice(service.key, Number(event.target.value))}
                                disabled={readOnly || !selected}
                                className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm font-semibold text-slate-900"
                              />
                            </div>
                            <div className="flex items-end justify-end">
                              {isCustom ? (
                                <button
                                  type="button"
                                  onClick={() => removeCustomService(service.key)}
                                  disabled={readOnly}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-200 bg-white text-rose-600 transition hover:bg-rose-50 disabled:opacity-50"
                                  title="Remove service"
                                >
                                  <Trash2 size={15} />
                                </button>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={addCustomService}
                      disabled={readOnly}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                    >
                      <Plus size={15} />
                      Add custom service
                    </button>
                    <p className="mt-2 text-xs text-slate-500">
                      Scheduled as {workspace.appointment.appointmentType.name}. Select the service delivered, or add another service if it happened during the visit.
                    </p>
                  </div>
                  <NoteBox label="Service notes" value={treatmentNotes} onChange={setTreatmentNotes} disabled={readOnly} placeholder="Optional: outcome, stock note, exception, payment context" rows={3} />
                  <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Products used</p>
                        <p className="mt-1 text-sm text-slate-500">Optional. Record products consumed during this visit so clinic stock is deducted correctly.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setStockUsage((current) => [...current, { itemName: '', plannedQuantity: 0, actualQuantity: 1 }])}
                        disabled={readOnly || !canAddStockUsage}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        title={canAddStockUsage ? 'Add product usage row' : stockUsageDisabledTitle}
                      >
                        <Plus size={15} />
                        Add product
                      </button>
                    </div>
                    {canAddStockUsage ? (
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm text-slate-500">Pick the actual product used so treatment completion can create the inventory consumption automatically.</p>
                        {manualLoadServices.length ? (
                          <button
                            type="button"
                            onClick={loadSelectedServiceProducts}
                            disabled={readOnly || !canManualLoadServiceProducts}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                            title={canManualLoadServiceProducts ? 'Load products linked to the selected manual services' : stockUsageDisabledTitle}
                          >
                            <Plus size={15} />
                            Load service products
                          </button>
                        ) : null}
                      </div>
                    ) : stockUsageAvailability === 'no-stock' ? (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        No stocked products are available at {treatmentLocationName} yet. Receive stock or complete an opening count first, then return here to record what was consumed.
                      </div>
                    ) : stockUsageAvailability === 'no-products' ? (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        No inventory products have been set up yet. Add products in Inventory Setup first, then receive stock before linking product consumption to visits.
                      </div>
                    ) : (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        This visit does not have an inventory location yet. Assign a location to the appointment before linking product consumption.
                      </div>
                    )}
                    {!stockUsage.length && !canAddStockUsage ? (
                      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                        {stockUsageAvailability === 'no-stock'
                          ? `No stocked products are currently available at ${treatmentLocationName}.`
                          : stockUsageAvailability === 'no-products'
                            ? 'No inventory products are configured yet.'
                            : 'No product consumption can be recorded here until the visit has a location.'}
                      </div>
                    ) : null}
                    {stockUsage.map((item, index) => {
                      const seenLots = new Set<string>();
                      const lotOptions = inventoryRows
                        .filter((row) => {
                          const key = row.lotId || row.lotNumber || '';
                          if (row.id !== item.itemId || !key || seenLots.has(key)) return false;
                          seenLots.add(key);
                          return true;
                        })
                        .map((row) => ({
                          value: row.lotId || '',
                          label: inventoryLotLabel(row),
                        }));
                      const unitOptions = (inventoryItemUnits[item.itemId || ''] || []).map((unit) => ({
                        value: unit.unitId,
                        label: unit.abbreviation || unit.name,
                      }));
                      return (
                        <div key={`${item.itemId || 'manual'}-${index}`} className="grid gap-3 rounded-xl border border-slate-200 p-3.5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_110px_110px_44px] md:items-center">
                          <select
                            value={item.itemId || ''}
                            onChange={(event) => selectInventoryItem(index, event.target.value)}
                            disabled={readOnly || !canAddStockUsage}
                            aria-label={`Product used row ${index + 1}`}
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50"
                          >
                            <option value="">Choose product</option>
                            {inventoryItems.map((inventoryItem) => (
                              <option key={inventoryItem.id} value={inventoryItem.id}>{inventoryItemLabel(inventoryItem)}</option>
                            ))}
                          </select>
                          <div>
                            <select
                              value={item.lotId || ''}
                              onChange={(event) => selectStockLot(index, item.itemId, event.target.value)}
                              disabled={readOnly || !item.itemId}
                              aria-label={`Lot for product row ${index + 1}`}
                              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50"
                            >
                              <option value="">Use oldest available lot automatically</option>
                              {lotOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={item.actualQuantity}
                            onChange={(event) => updateStockUsage(index, { actualQuantity: Number(event.target.value) })}
                            disabled={readOnly}
                            placeholder="Qty"
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50"
                          />
                          <select
                            value={item.unitId || ''}
                            onChange={(event) => selectStockUsageUnit(index, event.target.value)}
                            disabled={readOnly || !item.itemId || !unitOptions.length}
                            aria-label={`Unit row ${index + 1}`}
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50"
                          >
                            <option value="">{item.itemId ? 'Choose unit' : 'Select product first'}</option>
                            {unitOptions.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => setStockUsage((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                            disabled={readOnly}
                            className="inline-flex h-10 w-10 items-center justify-center self-center rounded-lg border border-rose-200 bg-white text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                            title="Remove product usage row"
                          >
                            <Trash2 size={16} />
                          </button>
                          <div className="rounded-lg bg-slate-50 px-3 py-2.5 text-xs text-slate-600 md:col-span-5">
                            Planned {item.plannedQuantity ?? item.actualQuantity} {item.unit || ''}
                            {item.lotNumber ? ` · Lot ${item.lotNumber}` : ''}
                            {item.brand ? ` · ${item.brand}` : ''}
                            {item.sourceServiceLabel ? ` · From ${item.sourceServiceLabel}` : ''}
                          </div>
                          {item.plannedQuantity !== undefined && item.actualQuantity !== item.plannedQuantity ? (
                            <input
                              value={item.deviationReason || ''}
                              onChange={(event) => updateStockUsage(index, { deviationReason: event.target.value })}
                              disabled={readOnly}
                              placeholder="Deviation reason"
                              className="md:col-span-5 w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100 disabled:bg-slate-50"
                            />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                  {(requiredSafetyChecks.length || adverseEventEnabled) && (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                      <p className="mb-3 text-sm font-semibold text-slate-900">Clinical safety</p>
                      <div className="space-y-2">
                        {requiredSafetyChecks.map((check) => (
                          <label key={check.key} className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              checked={Boolean(safetyChecks[check.key])}
                              onChange={(event) => setSafetyChecks((current) => ({ ...current, [check.key]: event.target.checked }))}
                              disabled={readOnly}
                              className="h-4 w-4 rounded border-slate-300 text-emerald-600"
                            />
                            {check.label}
                          </label>
                        ))}
                        {adverseEventEnabled && (
                          <>
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                              <input
                                type="checkbox"
                                checked={Boolean(safetyChecks.adverseEventOccurred)}
                                onChange={(event) => setSafetyChecks((current) => ({ ...current, adverseEventOccurred: event.target.checked }))}
                                disabled={readOnly}
                                className="h-4 w-4 rounded border-slate-300 text-emerald-600"
                              />
                              Adverse event recorded
                            </label>
                            {safetyChecks.adverseEventOccurred ? (
                              <textarea
                                value={String(safetyChecks.adverseEventNotes || '')}
                                onChange={(event) => setSafetyChecks((current) => ({ ...current, adverseEventNotes: event.target.value }))}
                                disabled={readOnly}
                                rows={2}
                                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                              />
                            ) : null}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  {canViewFinancial ? (
                    <>
                      <div className="rounded-2xl bg-slate-950 px-4 py-4 text-white shadow-sm">
                        <div className="flex items-center gap-2 text-slate-400">
                          <ReceiptText className="h-4 w-4" />
                          <p className="text-[11px] font-semibold uppercase tracking-[0.22em]">{invoiceNumber ? 'Invoice created' : 'Draft invoice'}</p>
                        </div>
                        <p className="mt-2 text-2xl font-semibold">{formatMoney(selectedServiceTotal, currency)}</p>
                        <p className="mt-1 text-xs text-slate-400">Selected service total before adjustments</p>
                        {invoiceNumber ? (
                          <button
                            type="button"
                            onClick={openInvoice}
                            className="mt-3 inline-flex rounded-lg bg-white/10 px-2 py-1 text-xs text-slate-200 transition hover:bg-white/20"
                          >
                            Invoice {invoiceNumber}
                          </button>
                        ) : (
                          <p className="mt-3 rounded-lg bg-white/10 px-2 py-1 text-xs text-slate-200">
                            Saving treatment creates the invoice lines.
                          </p>
                        )}
                        {paymentBalance && paymentBalance > 0 ? (
                          <p className="mt-3 rounded-lg bg-white/10 px-2 py-1 text-xs text-slate-200">
                            Existing balance: {formatMoney(paymentBalance, currency)}
                          </p>
                        ) : null}
                      </div>
                      {invoiceNumber ? (
                        <button
                          type="button"
                          onClick={openInvoice}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                        >
                          <ReceiptText size={15} />
                          Open billing invoice
                        </button>
                      ) : null}
                      <div className={cn(
                        'rounded-2xl border p-3',
                        packageWillBeUsed ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white',
                      )}>
                        <div className="mb-2 flex items-center gap-2">
                          <Gift className="h-4 w-4 text-emerald-600" />
                          <span className="text-sm font-semibold text-slate-900">Package credit</span>
                        </div>
                        {eligiblePackages.length ? (
                          <div className="space-y-2">
                            <SelectMenu
                              value={selectedPackageId}
                              onChange={(value) => setSelectedPackageId(value)}
                              disabled={readOnly || packageWillBeUsed}
                              buttonAriaLabel="Package to use"
                              options={eligiblePackages.map((pkg) => ({
                                value: pkg.id,
                                label: `${pkg.serviceName} · ${pkg.remainingQuantity} remaining${pkg.expiresAt ? ` · expires ${new Date(pkg.expiresAt).toLocaleDateString()}` : ''}`,
                              }))}
                            />
                            {packageWillBeUsed ? (
                              <p className="rounded-xl bg-white/70 px-3 py-2 text-xs text-emerald-700">
                                {selectedPackage ? `${selectedPackage.serviceName} will be consumed when treatment is saved.` : 'Package will be consumed when treatment is saved.'}
                              </p>
                            ) : (
                              <button
                                type="button"
                                onClick={useSelectedPackageForVisit}
                                disabled={readOnly || !selectedPackageId}
                                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-50 disabled:opacity-50"
                              >
                                <Gift size={15} />
                                Use package for this visit
                              </button>
                            )}
                          </div>
                        ) : paymentStatus === 'PACKAGE_REDEEMED' ? (
                          <p className="text-xs text-emerald-700">This visit is marked as covered by a package.</p>
                        ) : (
                          <p className="text-xs text-slate-500">No active package matches this patient and scheduled service.</p>
                        )}
                      </div>
                      <label className="block">
                        <span className="mb-1.5 block text-sm font-medium text-slate-700">Payment status</span>
                        <SelectMenu
                          value={paymentStatus}
                          onChange={(value) => {
                            setPaymentStatus(value as PaymentStatus);
                            if (value !== 'PACKAGE_REDEEMED') setPackageWillBeUsed(false);
                          }}
                          disabled={readOnly}
                          buttonAriaLabel="Payment status"
                          options={paymentStatuses.map((status) => ({
                            value: status,
                            label: paymentStatusLabels[status],
                          }))}
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1.5 block text-sm font-medium text-slate-700">Amount to bill</span>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={finalAmount}
                          onChange={(event) => handleFinalAmountChange(Number(event.target.value))}
                          disabled={readOnly}
                          className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm shadow-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                        />
                      </label>
                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                        Adjustment: {formatMoney(finalAmount - selectedServiceTotal, currency)}
                      </div>
                    </>
                  ) : (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                      Payment details restricted
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={completeTreatment}
                    disabled={readOnly || busyAction === 'treatment'}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {busyAction === 'treatment' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard size={16} />}
                    {canViewFinancial ? 'Save treatment and draft invoice' : 'Save treatment'}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500">This visit is not linked to an appointment, so treatment/payment actions are not available yet.</p>
            )}
          </section>
        </div>

        <aside className="space-y-5 xl:sticky xl:top-6 xl:self-start">
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-start gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-50 text-slate-700 ring-1 ring-slate-200">
                <UserRound className="h-5 w-5" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Patient</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-950">Patient context</h2>
              </div>
            </div>
            <div className="space-y-4">
              <InfoRow label="MRN" value={workspace.patient.mrn || '-'} />
              <InfoRow label="DOB" value={workspace.patient.dob ? new Date(workspace.patient.dob).toLocaleDateString() : '-'} />
              <InfoRow label="Sex" value={workspace.patient.sex || '-'} />
              <InfoRow label="Phone" value={workspace.patient.phone || '-'} />
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Allergies</p>
                {listPreview(workspace.patient.allergies, 'No allergies recorded.')}
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Problems</p>
                {listPreview(workspace.patient.problems, 'No active problems recorded.')}
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Medications</p>
                {listPreview(workspace.patient.medications, 'No medications recorded.')}
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-start gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-50 text-cyan-700 ring-1 ring-slate-200">
                <CalendarDays className="h-5 w-5" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-700">Schedule</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-950">Appointment</h2>
              </div>
            </div>
            {workspace.appointment ? (
              <div className="space-y-3 text-sm">
                <InfoRow label="Service" value={workspace.appointment.appointmentType.name} />
                <InfoRow label="Time" value={formatDateTime(workspace.appointment.startTime)} />
                <InfoRow label="Provider" value={workspace.provider.name} />
                <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-slate-600">
                  <MapPin className="mt-0.5 h-4 w-4 text-slate-400" />
                  <span>{workspace.appointment.location?.name || 'No location'}</span>
                </div>
                {workspace.appointment.notes ? <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-600">{workspace.appointment.notes}</p> : null}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No appointment linked.</p>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">History</p>
              <h2 className="mt-1 text-lg font-semibold text-slate-950">Recent visits</h2>
            </div>
            <div className="mt-4 space-y-3">
              {workspace.recentVisits.length ? (
                workspace.recentVisits.map((visit) => (
                  <div key={visit.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-slate-900">{visit.appointmentType || 'Visit'}</p>
                      <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-500">{titleCase(visit.status)}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">{formatDateTime(visit.completedAt || visit.updatedAt)}</p>
                    {visit.visitReason ? <p className="mt-2 text-sm text-slate-600">{visit.visitReason}</p> : null}
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">No recent completed visits.</p>
              )}
            </div>
          </section>
        </aside>
      </div>
      </main>
    </div>
  );
}

function StatusTile({ label, value, tone = 'slate' }: { label: string; value: string; tone?: 'slate' | 'emerald' | 'amber' }) {
  const toneStyles = {
    slate: 'border-slate-200 bg-slate-50 text-slate-900',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-950',
    amber: 'border-amber-200 bg-amber-50 text-amber-950',
  } as const;

  return (
    <div className={cn('rounded-xl border px-3 py-2', toneStyles[tone])}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-xs font-semibold leading-5 text-current">{value}</p>
    </div>
  );
}

function ActionButton({
  icon: Icon,
  label,
  description,
  busy,
  tone = 'primary',
  onClick,
}: {
  icon: any;
  label: string;
  description?: string;
  busy?: boolean;
  tone?: 'primary' | 'muted' | 'danger';
  onClick: () => void;
}) {
  const toneStyles = {
    primary: {
      button: 'border-slate-200 bg-white text-slate-900 hover:border-slate-300 hover:bg-slate-50',
      icon: 'text-slate-900',
    },
    muted: {
      button: 'border-slate-200 bg-slate-50 text-slate-800 hover:border-slate-300 hover:bg-white',
      icon: 'text-slate-600',
    },
    danger: {
      button: 'border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100',
      icon: 'text-rose-700',
    },
  } as const;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={cn(
        'inline-flex h-full w-full items-start gap-2 rounded-xl border px-3 py-2.5 text-left transition disabled:cursor-not-allowed disabled:opacity-60',
        toneStyles[tone].button,
      )}
    >
      <span className="mt-0.5 shrink-0">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon size={16} className={toneStyles[tone].icon} />}</span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold">{label}</span>
        {description ? <span className="mt-0.5 block text-xs font-medium leading-4 text-slate-500">{description}</span> : null}
      </span>
    </button>
  );
}

function NoteBox({
  label,
  value,
  onChange,
  disabled,
  placeholder,
  rows = 5,
  focusKey,
  onFocusField,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
  focusKey?: string;
  onFocusField?: (key: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-700">{label}</span>
      <textarea
        data-treatment-focus={focusKey}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => {
          if (focusKey && onFocusField) onFocusField(focusKey);
        }}
        disabled={disabled}
        rows={rows}
        placeholder={placeholder}
        className="w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50"
      />
    </label>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
      <span className="font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-800">{value}</span>
    </div>
  );
}
